import { inject, Injectable, InjectionToken } from '@angular/core';
import { map, ReplaySubject } from 'rxjs';

import { Persist } from '../storage/persist.model';
import { AuthRefreshToken, AuthRefreshTokenPayload } from './auth-token/auth-refresh-token.model';
import { AuthStaticToken } from './auth-token/auth-static-token.model';
import { AbstractLogoutAdaptor } from './logout/abstract-logout.adaptor';

export type AuthToken = AuthStaticToken | AuthRefreshToken | null;

export const AUTH_PERSIST = new InjectionToken<Persist<string | AuthRefreshTokenPayload | null>>('AUTH_PERSIST');

@Injectable()
export class AuthService {
  private authPersist = inject(AUTH_PERSIST);
  private logoutAdaptor = inject(AbstractLogoutAdaptor, { optional: true });

  private _tokenInstance: AuthToken;

  get tokenInstance(): AuthToken {
    return this._tokenInstance;
  }

  private _token = new ReplaySubject<string | null>(1);
  readonly token$ = this._token.asObservable();

  readonly authenticated = this.token$.pipe(map(() => !!this.tokenInstance?.isValid()));

  async init() {
    const authObj = await this.authPersist.read((value) => {
      if (value != null && typeof value === 'object') {
        return {
          ...value,
          accessTokenExpiry: new Date(value.accessTokenExpiry),
          refreshTokenExpiry: new Date(value.refreshTokenExpiry),
        };
      }
      return value;
    });

    if (authObj == null) return this._token.next(null);

    this.setToken(authObj);
  }

  setToken(token: string | AuthRefreshTokenPayload) {
    if (typeof token === 'string') {
      this._tokenInstance = new AuthStaticToken(token);
      this._token.next(token);
    } else {
      this._tokenInstance = new AuthRefreshToken(token);
      this._token.next(token.accessToken);
    }
    this.persist();
  }

  logout(source = 'logout') {
    this.removeToken();
    this.logoutAdaptor?.onLogout(source);
  }

  isTokenTypeRefreshToken(): this is { tokenInstance: AuthRefreshToken } {
    return this.tokenInstance instanceof AuthRefreshToken;
  }

  private removeToken() {
    this._tokenInstance = null;
    this._token.next(null);
    this.authPersist.clear();
  }

  private persist() {
    if (this.tokenInstance instanceof AuthStaticToken) {
      this.authPersist.write(this.tokenInstance.token);
    } else if (this.tokenInstance instanceof AuthRefreshToken) {
      this.authPersist.write(this.tokenInstance);
    }
  }
}
